import { useState, useEffect } from 'react'

/**
 * A custom hook to handle local storage operations.
 *
 * @param {string} key - The key to store the value under.
 * @param {any} defaultValue - The default value to use if the key is not found in local storage.
 * @param {number|null} daysToExpire - The number of days until the value expires. If null, the value will not expire.
 *
 * @returns {[any, function]} - An array containing the current value and a function to set the value.
 */
const useLocalStorage = (
  key: string,
  defaultValue: any,
  daysToExpire: number | null = null
) => {
  /**
   * Initialize the state with the current value from local storage or the default value.
   */
  const [value, setValue] = useState(() => {
    let currentValue

    try {
      currentValue = JSON.parse(
        localStorage.getItem(key) || String(defaultValue)
      )

      // Check if the item has expired and remove it if it has
      if (currentValue && currentValue?.expiry !== null) {
        const now = new Date()
        const expiryDate = new Date(currentValue.expiry)
        if (now.getTime() > expiryDate.getTime()) {
          localStorage.removeItem(key)
          currentValue = defaultValue
        } else {
          currentValue = currentValue.value
        }
      }
    } catch (error) {
      currentValue = defaultValue
    }

    if (currentValue?.value) {
      return currentValue.value
    }

    return currentValue
  })

  /**
   * Update local storage whenever the value changes.
   */
  useEffect(() => {
    if (value === undefined || (Array.isArray(value) && value.length === 0)) {
      localStorage.removeItem(key)
    } else {
      // If daysToExpire is null, the item will not expire
      let expiryDate = null
      if (daysToExpire !== null) {
        const now = new Date()
        expiryDate = new Date(
          now.getTime() + daysToExpire * 24 * 60 * 60 * 1000
        )
      }

      if (value?.value) {
        localStorage.setItem(
          key,
          JSON.stringify({ value: value.value, expiry: expiryDate })
        )
      } else {
        localStorage.setItem(key, JSON.stringify({ value, expiry: expiryDate }))
      }
    }
  }, [value, key, daysToExpire])

  if (value?.value) {
    return [value.value, setValue]
  }

  return [value, setValue]
}

export default useLocalStorage
